<template>
  <div v-if="hasAssets" class="offer-assets" :style="customStyle">
    <!-- Main Asset (Gold or first asset if no gold) -->
    <div
      v-if="mainAsset"
      class="asset-item asset-item-main"
      :class="{ 'asset-item-single': isSingleAsset }"
    >
      <div
        class="asset-icon"
        :class="getAssetIconClass(mainAsset)"
        :style="getAssetIconStyle(mainAsset)"
      ></div>
      <div class="asset-amount">
        {{ formatAssetAmountWithSuffix(mainAsset) }}
      </div>
    </div>

    <!-- Other Assets Row -->
    <div v-if="otherAssets.length > 0" class="asset-row-other">
      <div
        v-for="(asset, index) in otherAssets"
        :key="`other-${index}`"
        class="asset-item asset-item-other"
      >
        <div
          class="asset-icon"
          :class="getAssetIconClass(asset)"
          :style="getAssetIconStyle(asset)"
        ></div>
        <div class="asset-amount">
          {{ formatAssetAmountWithSuffix(asset) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "OfferPackageAssets",
  props: {
    package: {
      type: Object,
      required: true,
    },
    customStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    hasAssets() {
      return this.package?.assets && this.package.assets.length > 0;
    },
    /**
     * Check if there's only a single asset in the package
     */
    isSingleAsset() {
      return this.package?.assets?.length === 1;
    },
    /**
     * Main asset to display (gold if available, otherwise first asset)
     */
    mainAsset() {
      if (!this.hasAssets) return null;
      const goldAsset = this.package.assets.find((a) => a.type === "gold");
      return goldAsset || this.package.assets[0];
    },
    /**
     * Gold assets for backward compatibility (returns array with main asset if it's gold)
     */
    goldAssets() {
      if (!this.hasAssets) return [];
      const goldAsset = this.package.assets.find((a) => a.type === "gold");
      return goldAsset ? [goldAsset] : [];
    },
    /**
     * Other assets (excluding the main asset)
     */
    otherAssets() {
      if (!this.hasAssets) return [];
      if (this.mainAsset) {
        return this.package.assets.filter((a) => a !== this.mainAsset);
      }
      return this.package.assets.slice(1);
    },
  },
  methods: {
    formatAssetAmount(amount) {
      const n = Number(amount);
      if (!Number.isFinite(n)) return amount;

      const raw = this.$store?.state?.locale || "de";
      const locale = raw === "de" ? "de-DE" : "en-US";

      return new Intl.NumberFormat(locale, {
        useGrouping: true,
        maximumFractionDigits: 0,
      }).format(n);
    },
    getAssetIconStyle(asset) {
      const url = this.getWhitelabelIconUrl(asset);
      return url ? { backgroundImage: `url(${url})` } : {};
    },
    getWhitelabelIconUrl(asset) {
      const type = (asset?.type || "").toLowerCase();

      if (type === "gold") return this.$whitelabel?.currencies?.primary?.triple;
      if (type === "crown")
        return this.$whitelabel?.currencies?.secondary?.accent;
      if (type === "loyalty")
        return this.$whitelabel?.assets?.features?.loyalty?.icon;

      if (type === "elixir")
        return this.$whitelabel?.currencies?.tertiary?.single;
      if (!asset?.type && asset?.asset?.endsWith("Elixir"))
        return this.$whitelabel?.currencies?.tertiary?.single;

      if (asset?.asset?.endsWith("Card"))
        return this.$whitelabel?.assets?.features?.cards?.icon;

      // fallback
      return null;
    },
    /**
     * Get asset icon class name
     * If asset.type is null/undefined, extract the last word from asset.asset (PHP namespace)
     * @param {Object} asset - Asset object with type and/or asset property
     * @returns {string} CSS class name for the asset icon
     */
    getAssetIconClass(asset) {
      if (asset.asset && asset.asset.toLowerCase().includes("\\card")) {
        return "asset-icon-card";
      }
      if (asset.type) {
        return `asset-icon-${asset.type}`;
      }

      // Fallback: extract last word from PHP namespace class
      if (asset.asset) {
        const className = asset.asset.split("\\").pop().toLowerCase();
        return `asset-icon-${className}`;
      }

      return "asset-icon-unknown";
    },
    formatAssetAmountWithSuffix(asset) {
      if (!asset) return "";
      const formatted = this.formatAssetAmount(asset.amount);

      const type = (asset?.type || "").toLowerCase();
      const isGold =
        type === "gold" || asset?.asset?.toLowerCase().includes("\\gold");

      return isGold ? formatted : `${formatted}x`;
    },
  },
};
</script>

<style scoped lang="scss">
// Asset icon styling - background images for different asset types
.asset-icon {
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;

  &.asset-icon-gold {
    transform: scale(1.5);
    transform-origin: center;
    background-image: url("~@/assets/img/common/coin.png");
  }

  &.asset-icon-crown {
    background-image: url("~@/assets/img/common/buzzer_gem.png"); // Placeholder, replace with crown icon URL
  }

  &.asset-icon-loyalty {
    background-image: url("~@/assets/img/common/loyalty.png");
  }

  &.asset-icon-elixir {
    background-image: url("~@/assets/img/common/elixir.png");
  }

  &.asset-icon-card {
    background-image: url("~@/assets/img/common/cardstack-3.png");
  }
}

// Asset amount text styling
.asset-amount {
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 10px rgba(0, 0, 0, 0.5);
}

// Base asset container styling (positioning is layout-specific)
.offer-assets {
  position: absolute;
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  .asset-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
  }

  .asset-item-main {
    width: 100%;
    height: 60%;

    &.asset-item-single {
      height: 100%;
    }
  }

  .asset-row-other {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 40%;
  }

  .asset-item-other {
    flex: 1;
    height: 100%;
  }
}
</style>
